import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  Clock,
  AlertCircle,
  CheckCircle,
  Send,
  MessageSquare,
} from 'lucide-react';
import { notificationsAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

// Types
type NotificationType =
  | 'post_published'
  | 'post_failed'
  | 'post_scheduled'
  | 'team_invite'
  | 'approval_request'
  | 'comment';

type FilterType = 'all' | 'unread' | 'posts' | 'team';

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  description: string;
  timestamp: string;
  read: boolean;
  link?: string;
}

// Utility functions
const getTimeAgo = (dateStr: string): string => {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const getNotificationIcon = (type: NotificationType): React.ReactNode => {
  switch (type) {
    case 'post_published':
      return <CheckCircle size={18} className="text-n-green" />;
    case 'post_failed':
      return <AlertCircle size={18} className="text-n-red" />;
    case 'post_scheduled':
      return <Clock size={18} className="text-n-amber" />;
    case 'team_invite':
      return <Send size={18} className="text-n-blue" />;
    case 'approval_request':
      return <AlertCircle size={18} className="text-n-blue" />;
    case 'comment':
      return <MessageSquare size={18} className="text-n-blue" />;
    default:
      return <Bell size={18} className="text-n-text-lt" />;
  }
};

const getNotificationBgColor = (read: boolean): string => {
  if (read) return 'bg-n-card hover:bg-n-hover';
  return 'bg-n-blue-bg/50 hover:bg-n-blue-bg';
};

const NotificationBell: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Only poll when the user is actually signed in. Without this gate
  // the bell keeps hammering /api/notifications with no token on every
  // page (login screen, logout state, or while the auth session is
  // still hydrating), producing a stream of 401s in the server log.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isAuthLoading = useAuthStore((s) => s.isLoading);

  // Fetch notifications from API
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsAPI.list({ limit: 20 }).then(r => r.data),
    staleTime: 2 * 60 * 1000, // 2 minutes
    // Poll every 2 minutes — only while authenticated. When unauthenticated,
    // `refetchInterval: false` stops the timer entirely.
    refetchInterval: isAuthenticated ? 2 * 60 * 1000 : false,
    refetchIntervalInBackground: false,
    // Gate the whole query on auth. Also wait out the initial
    // hydration so we don't fire a no-token request on mount.
    enabled: isAuthenticated && !isAuthLoading,
    // Don't retry on 401 — retrying just multiplies the noise and
    // burns request budget on a call we already know will fail.
    retry: (failureCount, error: unknown) => {
      const status = (error as { response?: { status?: number } } | undefined)
        ?.response?.status;
      if (status === 401 || status === 403) { return false; }
      return failureCount < 2;
    },
    refetchOnWindowFocus: isAuthenticated,
  });

  const notifications: Notification[] = useMemo(() => data?.notifications || [], [data?.notifications]);
  const unreadCount: number = data?.unread_count || 0;

  // Mutations
  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsAPI.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsAPI.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Filter notifications
  const filteredNotifications = useMemo(() => {
    let filtered = notifications;

    if (filter === 'unread') {
      filtered = filtered.filter((n) => !n.read);
    } else if (filter === 'posts') {
      filtered = filtered.filter((n) =>
        ['post_published', 'post_failed', 'post_scheduled'].includes(n.type)
      );
    } else if (filter === 'team') {
      filtered = filtered.filter((n) =>
        ['team_invite', 'approval_request'].includes(n.type)
      );
    }

    return filtered;
  }, [notifications, filter]);

  // Mark single notification as read
  const markAsRead = (id: string): void => {
    const notification = notifications.find(n => n.id === id);
    if (notification && !notification.read) {
      markReadMutation.mutate(id);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="n-btn-icon relative"
        aria-label={t('notif_aria_notifications', 'Notifications')}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold leading-none text-white bg-n-red rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="n-popover right-0 mt-2 w-96 max-h-[500px] flex flex-col"
        >
          {/* Header */}
          <div className="n-card-header">
            <h3 className="n-section-title">{t('notif_heading', 'Notifications')}</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllReadMutation.mutate()}
                className="text-xs font-medium text-n-blue hover:underline transition-colors"
              >
                {t('notif_mark_all_read', 'Mark all read')}
              </button>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="n-tab-bar px-3">
            {(['all', 'unread', 'posts', 'team'] as FilterType[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`n-tab ${filter === tab ? 'n-tab-active' : ''}`}
              >
                {tab === 'all'
                  ? t('notif_filter_all', 'All')
                  : tab === 'unread'
                    ? t('notif_filter_unread', 'Unread')
                    : tab === 'posts'
                      ? t('notif_filter_posts', 'Posts')
                      : t('notif_filter_team', 'Team')}
              </button>
            ))}
          </div>

          {/* Notification List */}
          <div className="flex-1 overflow-y-auto">
            {filteredNotifications.length > 0 ? (
              <div className="divide-y divide-n-border">
                {filteredNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    role="button"
                    tabIndex={0}
                    className={`px-4 py-3 transition-colors cursor-pointer ${getNotificationBgColor(notification.read)}`}
                    onClick={() => markAsRead(notification.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); markAsRead(notification.id); } }}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-[13px] font-medium text-n-text">
                            {notification.title}
                          </p>
                          <div className="flex-shrink-0">
                            {!notification.read && (
                              <div className="w-1.5 h-1.5 rounded-full bg-n-blue mt-1.5" />
                            )}
                          </div>
                        </div>
                        <p className="text-[13px] text-n-text-md mt-0.5">
                          {notification.description}
                        </p>
                        <p className="n-caption mt-1">
                          {getTimeAgo(notification.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="n-empty py-8">
                <div className="n-empty-icon"><Bell size={18} /></div>
                <p className="n-empty-desc">{t('notif_empty_state', 'No notifications')}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
